import {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useCallback,
} from 'react';
import { useAuth } from './AuthContext';
import { apiFetch } from '../apiClient';

export interface Contatto {
  id: number;
  first_name: string;
  last_name: string;
  email?: string;
  telefono?: string;
  settore?: string;
  indirizzo?: string;
  nome?: string;
  cognome?: string;
  azienda?: string;
  occupation?: string;
  location?: string;
  linkedin_url?: string;
  company_name?: string;
  associato?: string;
  servizio_proposto?: string;
  esito_contatto?: string;
  commento?: string;
  analisi_lead?: string;
  data_ultimo_contatto?: string;
  numero_contatti: number;
  preferito: boolean;
  tipo: 'contatto' | 'small-lead' | 'startup';
}


interface ContattiContextType {
  contatti: Contatto[];
  loading: boolean;
  error: string | null;
  aggiornaDati: () => Promise<void>;
  aggiornaAssociato: (id: number, nuovo: string, tipo: string) => Promise<void>;
  aggiornaServizio: (id: number, nuovo: string, tipo: string) => Promise<void>;
  aggiornaEsito: (id: number, nuovo: string, tipo: string) => Promise<void>;
  togglePreferito: (id: number, attuale: boolean, tipo: string) => Promise<void>;
  aggiornaNumeroContatti: (id: number, operazione: "+" | "-", tipo: string) => Promise<void>;
}

interface ContattiProviderProps {
  children: ReactNode;
}

export const ContattiContext = createContext<ContattiContextType | undefined>(
  undefined
);

export function ContattiProvider({ children }: ContattiProviderProps) {
  const { isAuthenticated } = useAuth();
  const [contatti, setContatti] = useState<Contatto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const caricaDati = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('contatti/');

      if (!res.ok) {
        if (res.status === 401) throw new Error('Sessione scaduta');
        throw new Error('Errore nel caricamento contatti');
      }

      const data = await res.json();
      setContatti(data.contatti);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const aggiornaAssociato = async (id: number, nuovo: string, tipo: string) => {
    if (!isAuthenticated) return;
    await apiFetch(`contatti/update_associato?tipo=${tipo}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contatto_id: id, nuovo_associato: nuovo }),
    });
    setContatti(prev => prev.map(c => c.id === id ? { ...c, associato: nuovo } : c));
  };

  const aggiornaServizio = async (id: number, nuovo: string, tipo: string) => {
    if (!isAuthenticated) return;
    await apiFetch(`contatti/update_servizio?tipo=${tipo}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contatto_id: id, nuovo_servizio: nuovo }),
    });
    setContatti(prev => prev.map(c => c.id === id ? { ...c, servizio_proposto: nuovo } : c));
  };

  const aggiornaEsito = async (id: number, nuovo: string, tipo: string) => {
    if (!isAuthenticated) return;
    await apiFetch(`contatti/update_esito?tipo=${tipo}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contatto_id: id, nuovo_esito: nuovo }),
    });
    setContatti(prev => prev.map(c => c.id === id ? { ...c, esito_contatto: nuovo } : c));
  };

  const togglePreferito = async (id: number, attuale: boolean, tipo: string) => {
    if (!isAuthenticated) return;
    await apiFetch(`contatti/${id}/preferito?tipo=${tipo}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ preferito: !attuale }),
    });
    setContatti(prev => prev.map(c => c.id === id ? { ...c, preferito: !attuale } : c));
  };

  const aggiornaNumeroContatti = async (id: number, operazione: "+" | "-", tipo: string) => {
    if (!isAuthenticated) return;
    const res = await apiFetch(
      `contatti/${id}/punteggio/?tipo=${encodeURIComponent(tipo)}&operazione=${encodeURIComponent(operazione)}`,
      { method: 'POST' }
    );
    const data = await res.json();
    setContatti(prev =>
      prev.map(c =>
        c.id === id
          ? {
              ...c,
              numero_contatti: data.nuovo_punteggio,
              data_ultimo_contatto:
                data.data_ultimo_contatto !== undefined
                  ? data.data_ultimo_contatto || undefined
                  : c.data_ultimo_contatto,
            }
          : c
      )
    );
  };

  useEffect(() => {
    if (isAuthenticated) caricaDati();
  }, [isAuthenticated, caricaDati]);

  return (
    <ContattiContext.Provider
      value={{
        contatti,
        loading,
        error,
        aggiornaDati: caricaDati,
        aggiornaAssociato,
        aggiornaServizio,
        aggiornaEsito,
        togglePreferito,
        aggiornaNumeroContatti,
      }}
    >
      {children}
    </ContattiContext.Provider>
  );
}

export function useContatti(): ContattiContextType {
  const context = useContext(ContattiContext);
  if (!context) throw new Error("useContatti deve essere utilizzato dentro ContattiProvider");
  return context;
}
